import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import festivalInfo from "../../programme/festivalInfo.json";
import { apmEnabled, initApm } from "../lib/apm";
import { detectAndSaveDeviceProfile } from "../lib/performance";

// Dismiss timeout: 23 hours in milliseconds
const DISMISS_TIMEOUT = 23 * 60 * 60 * 1000;

const sendConsentEvent = (deicision: "accept" | "reject") => {
  const timestamp = Date.now();
  const id = `u-${timestamp}`;
  localStorage.setItem("user_consent_id", id);
  if (deicision === "accept") {
    localStorage.setItem("cookie_consent_accepted", new Date(timestamp).toISOString());
    localStorage.setItem("cookie_consent_status", "accepted");
  } else {
    localStorage.setItem("cookie_consent_status", "declined");
  }

  // Send best-effort request to /data-policy
  fetch("/data-policy", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      timestamp,
      id,
      deicision,
    }),
    keepalive: true,
  }).catch(() => {});
};

export const CookieConsent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCookieSection, setShowCookieSection] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if consent has already been decided (accepted or declined)
    const status = localStorage.getItem("cookie_consent_status");
    const dismissedAtStr = localStorage.getItem("cookie_consent_dismissed_at");

    let isDismissed = false;
    if (dismissedAtStr) {
      const dismissedAt = new Date(dismissedAtStr).getTime();
      if (Date.now() - dismissedAt < DISMISS_TIMEOUT) {
        isDismissed = true;
      }
    }

    // Only show if no decision (accepted or declined) has been saved in localStorage,
    // and if the dismiss timeout is off.
    if (!status && !isDismissed) {
      // Trigger performance benchmark during splash screen
      detectAndSaveDeviceProfile().catch(console.error);

      // Delay slightly for a smoother entry transition
      const timer = setTimeout(() => {
        setIsOpen(true);
        setShowCookieSection(apmEnabled);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    sendConsentEvent("accept");

    // Only hide the bottom part of the combined box, keep the splash up
    setShowCookieSection(false);
    initApm();
  };

  const handleDecline = () => {
    sendConsentEvent("reject");

    // Only hide the bottom part of the combined box, keep the splash up
    setShowCookieSection(false);
  };

  const handleGoToProgram = () => {
    localStorage.setItem("cookie_consent_dismissed_at", new Date().toISOString());
    setIsOpen(false);
    navigate("/");
  };

  const handleGoToInfo = () => {
    localStorage.setItem("cookie_consent_dismissed_at", new Date().toISOString());
    setIsOpen(false);
    navigate("/info");
  };

  const handleOutsideClick = () => {
    localStorage.setItem("cookie_consent_dismissed_at", new Date().toISOString());
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleOutsideClick();
    };

    window.addEventListener("keydown", handleEscape);

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const ticketsUrl = festivalInfo.links?.tickets?.url || "https://www.tixa.hu/tilos";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          onClick={handleOutsideClick}
        >
          {/* Modal Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="w-full max-w-[360px] sm:max-w-[400px] rounded-3xl overflow-hidden relative border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.8)] bg-background flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Upper Container: Splash Image & Greeting Shortcuts */}
            {/* aspect-square makes it taller to display more of the image */}
            <div className="relative aspect-square w-full overflow-hidden">
              <img
                src="/splash-small.jpg"
                alt="Tilos Maraton"
                className="w-full h-full object-cover"
              />

              <div className="absolute inset-0 flex flex-col justify-end p-5">
                {/* Shortcuts */}
                <div className="flex gap-2 justify-center w-full">
                  <a
                    href={ticketsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2.5 px-2 text-center font-['Unbounded'] text-[9px] sm:text-[10px] font-bold tracking-wider uppercase rounded-xl bg-[#808080]/75 text-white border border-white/10 hover:bg-[#808080]/70 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer flex items-center justify-center"
                  >
                    Jegyek
                  </a>
                  <button
                    type="button"
                    onClick={handleGoToProgram}
                    className="flex-1 py-2.5 px-2 font-['Unbounded'] text-[9px] sm:text-[10px] font-bold tracking-wider uppercase rounded-xl bg-[#808080]/75 text-white border border-white/10 hover:bg-[#808080]/70 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer flex items-center justify-center"
                  >
                    Program
                  </button>
                  <button
                    type="button"
                    onClick={handleGoToInfo}
                    className="flex-1 py-2.5 px-2 font-['Unbounded'] text-[9px] sm:text-[10px] font-bold tracking-wider uppercase rounded-xl bg-main text-[#222222] shadow-[0_4px_12px_rgba(239,91,93,0.15)] hover:shadow-[0_4px_18px_rgba(239,91,93,0.35)] hover:bg-main hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer flex items-center justify-center"
                  >
                    Infó
                  </button>
                </div>
              </div>
            </div>

            {/* Lower Container: Privacy Banner / Cookie Consent */}
            {showCookieSection && (
              <div className="p-4 sm:p-5 bg-background border-t border-white/5 flex flex-col items-center text-center">
                {/* Title */}
                <h2 className="font-['Unbounded'] text-[10px] font-bold text-white uppercase tracking-wider mb-1">
                  Adatvédelem
                </h2>

                {/* Description */}
                <p className="text-[9px] sm:text-[10px] text-muted leading-relaxed mb-4 font-medium max-w-[90%]">
                  A weboldal használati adatokat és hibaadatokat gyűjt, hogy megértsük hogyan
                  használjátok, vagy mikor romlik el.
                  <br />
                  Ha érdekel pontosan mit, és hogy,{" "}
                  <a
                    href="/cookie-policy.md"
                    className="underline text-white/80 hover:text-white transition-colors"
                  >
                    kattints ide.
                  </a>
                </p>

                {/* Action Buttons */}
                <div className="flex w-full gap-3">
                  <button
                    type="button"
                    onClick={handleDecline}
                    className="flex-1 py-2 px-3 font-['Unbounded'] text-[9px] sm:text-[10px] font-bold tracking-widest uppercase rounded-xl border border-white/10 bg-[#808080]/50 text-muted hover:bg-[#808080]/70 hover:text-white transition-all duration-300 cursor-pointer"
                  >
                    Elutasítom
                  </button>
                  <button
                    type="button"
                    onClick={handleAccept}
                    className="flex-1 py-2 px-3 font-['Unbounded'] text-[9px] sm:text-[10px] font-bold tracking-widest uppercase rounded-xl bg-main text-[#222222] shadow-[0_0_15px_rgba(34,211,186,0.3)] hover:shadow-[0_0_25px_rgba(34,211,186,0.6)] hover:bg-[#22D3BA] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer"
                  >
                    Elfogadom
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
